import { useState, useRef, useCallback, useEffect } from "react";
import { SignatureEnums } from "../../enums/SignatureEnums";
import { message } from "antd";
import { Storage } from "aws-amplify";
import { textToImageConvertor } from "../../utils/textToImageConvertor";

export const useQuoteSignature = ({
  signatureState,
  onSignatureUpdate,
  setValue,
  getValues,
}) => {
  const sigCanvasRef = useRef(null);

  const [isSignUploadOpen, setIsSignUploadOpen] = useState(false);
  const [signatureType, setSignatureType] = useState(
    SignatureEnums.MANUAL_SIGNATURE
  );
  const [signatureFile, setSignatureFile] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [tempSignatureText, setTempSignatureText] = useState("");
  const [tempCanvasData, setTempCanvasData] = useState(null);
  const [uploadedSignatureFileList, setUploadedSignatureFileLits] = useState(
    []
  );
  const [isSubmitting, setIsSubmitting] = useState(false); // Add loading state

  const handleSignUploadModalOpen = useCallback(() => {
    if (signatureState?.type) {
      setSignatureType(signatureState.type);
    }
    if (signatureState?.textValue) {
      setTempSignatureText(signatureState.textValue);
      setValue("_signatureText", signatureState.textValue);
    }
    setIsSignUploadOpen(true);
    setIsSubmitting(false);
  }, [signatureState, setValue]);

  const handleSignUploadModalClose = useCallback(() => {
    setIsSignUploadOpen(false);
    setIsSubmitting(false);
  }, []);

  const handleSignatureTypeChange = useCallback((value) => {
    setSignatureType(value);
  }, []);

  const handleSignatureFileChange = useCallback((file) => {
    setSignatureFile(file);
  }, []);

  const handleOpenUploadModal = useCallback(() => {
    setIsUploadModalOpen(true);
  }, []);

  const handleCloseUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);

  const handleSignUploadModalSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);

      let finalUrl = null;
      let finalType = signatureType;
      let finalTextValue = null;

      if (signatureType === SignatureEnums.DIGITAL_SIGNATURE) {
        if (sigCanvasRef.current.isEmpty()) {
          message.error("Please sign before uploading.");
          setIsSubmitting(false);
          return;
        }
        const freeHandSign = sigCanvasRef.current.toDataURL("image/png");

        setTempCanvasData(freeHandSign);

        const blob = await fetch(freeHandSign).then((response) =>
          response.blob()
        );
        const filename = `signature-${Date.now()}.png`;
        const result = await Storage.put(filename, blob, {
          contentType: "image/png",
        });
        finalUrl = await Storage.get(result.key);
        setValue("_signatureText", "");
        setSignatureFile(null);
        setUploadedSignatureFileLits([]);
      } else if (signatureType === SignatureEnums.MANUAL_SIGNATURE) {
        const signatureName = getValues("_signatureText");

        if (!signatureName && !signatureFile?.docAttachment) {
          message.error(
            "Please enter a name or upload a file for the signature."
          );
          setIsSubmitting(false);
          return;
        }
        if (signatureName) {
          setTempSignatureText(signatureName);
          const textImageUrl = textToImageConvertor(signatureName);
          const blob = await fetch(textImageUrl).then((response) =>
            response.blob()
          );
          const filename = `signature-${Date.now()}.png`;
          const result = await Storage.put(filename, blob, {
            contentType: "image/png",
          });
          finalUrl = await Storage.get(result.key);
          finalTextValue = signatureName;
          setSignatureFile(null);
          setUploadedSignatureFileLits([]);
          sigCanvasRef.current?.clear();
          setTempCanvasData(null);
        } else if (signatureFile?.docAttachment) {
          finalUrl = signatureFile.docAttachment;
          setValue("_signatureText", "");
          sigCanvasRef.current?.clear();
          setTempCanvasData(null);
          setTempSignatureText("");
          setValue("_signatureText", undefined);
        }
      }

      if (finalUrl && onSignatureUpdate) {
        await onSignatureUpdate(finalUrl, finalType, finalTextValue);
        message.success("Signature uploaded successfully!");
      }

      setIsSignUploadOpen(false);
      setIsSubmitting(false);
      setSignatureFile(null);
    } catch (error) {
      console.error("Error uploading signature:", error);
      message.error("Failed to upload signature. Please try again.");
      setIsSubmitting(false);
    }
  }, [signatureType, signatureFile, onSignatureUpdate, setValue, getValues]);

  useEffect(() => {
    if (
      isSignUploadOpen &&
      signatureType === SignatureEnums.DIGITAL_SIGNATURE &&
      tempCanvasData &&
      sigCanvasRef.current
    ) {
      sigCanvasRef.current.clear();
      sigCanvasRef.current.fromDataURL(tempCanvasData);
    }
  }, [isSignUploadOpen, signatureType, tempCanvasData]);

  return {
    isSignUploadOpen,
    signatureType,
    signatureFile,
    isUploadModalOpen,
    tempSignatureText,
    tempCanvasData,
    uploadedSignatureFileList,
    sigCanvasRef,
    isSubmitting,
    handleSignUploadModalOpen,
    handleSignUploadModalClose,
    handleSignatureTypeChange,
    handleSignatureFileChange,
    handleOpenUploadModal,
    handleCloseUploadModal,
    handleSignUploadModalSubmit,
    setTempSignatureText,
    setTempCanvasData,
  };
};
